import { LATE_NIGHT_END_HOUR, LATE_NIGHT_START_HOUR, type OperatesFrom } from './constants';
import type { SellerSafetyPreferences, UserDoc } from './types';

export const CUSTOMISATION_NOTES_MAX_CHARS = 500;

const PROFANITY_BLOCKLIST = [
  'fuck',
  'shit',
  'bitch',
  'cunt',
  'nigger',
  'kaffir',
  'poes',
  'moer',
];

export function isLateNightSlot(date: Date): boolean {
  const hour = date.getHours();
  return hour >= LATE_NIGHT_START_HOUR || hour < LATE_NIGHT_END_HOUR;
}

export function containsBlockedLanguage(text: string | undefined | null): boolean {
  if (!text?.trim()) return false;
  const lower = text.toLowerCase();
  return PROFANITY_BLOCKLIST.some((w) => lower.includes(w));
}

export function defaultSellerSafetyPreferences(): SellerSafetyPreferences {
  return {
    requireSharedSpotForFirstBooking: true,
  };
}

export function sellerSafetyBadges(
  prefs: SellerSafetyPreferences | undefined,
): string[] {
  const badges: string[] = [];
  if (prefs?.womenClientsOnly) badges.push('Women clients only');
  if (prefs?.sameInstitutionOnly) badges.push('Same institution only');
  if (prefs?.requireSharedSpotForFirstBooking) badges.push('First booking: public spot');
  if (prefs?.noResidenceCallsAfterHour != null) {
    badges.push(`No residence calls after ${prefs.noResidenceCallsAfterHour}:00`);
  }
  return badges;
}

export interface BookingSafetyCheckInput {
  buyer: Pick<UserDoc, 'uid' | 'gender' | 'phoneNumber' | 'studentVerification'>;
  seller: Pick<UserDoc, 'uid' | 'studentVerification' | 'sellerProfile'>;
  locationType: OperatesFrom;
  scheduledStart: Date;
  priorCompletedTogether: number;
  serviceAfterHoursAvailable: boolean;
}

export type BookingSafetyViolation =
  | 'phone_required'
  | 'verification_expired'
  | 'women_clients_only'
  | 'same_institution_only'
  | 'first_booking_location'
  | 'residence_curfew'
  | 'late_night_unavailable'
  | 'blocked_language';

export function checkBookingSafetyRules(
  input: BookingSafetyCheckInput,
): BookingSafetyViolation | null {
  if (!input.buyer.phoneNumber?.trim()) return 'phone_required';

  const expiresAt = input.buyer.studentVerification.expiresAt;
  if (expiresAt && expiresAt.toMillis() < Date.now()) return 'verification_expired';

  const sellerExpires = input.seller.studentVerification.expiresAt;
  if (sellerExpires && sellerExpires.toMillis() < Date.now()) return 'verification_expired';

  const prefs = input.seller.sellerProfile?.safetyPreferences;
  if (prefs?.womenClientsOnly && input.buyer.gender === 'male') {
    return 'women_clients_only';
  }

  const buyerInst = input.buyer.studentVerification.institution;
  const sellerInst = input.seller.studentVerification.institution;
  if (prefs?.sameInstitutionOnly && buyerInst && sellerInst && buyerInst !== sellerInst) {
    return 'same_institution_only';
  }

  const firstBooking =
    prefs?.requireSharedSpotForFirstBooking !== false && input.priorCompletedTogether === 0;
  if (firstBooking && input.locationType === 'buyer_location') {
    return 'first_booking_location';
  }

  const curfew = prefs?.noResidenceCallsAfterHour;
  if (
    curfew != null &&
    input.locationType === 'buyer_location' &&
    input.scheduledStart.getHours() >= curfew
  ) {
    return 'residence_curfew';
  }

  if (isLateNightSlot(input.scheduledStart) && !input.serviceAfterHoursAvailable) {
    return 'late_night_unavailable';
  }

  return null;
}

export const BOOKING_SAFETY_MESSAGES: Record<BookingSafetyViolation, string> = {
  phone_required: 'Add a phone number to your profile before booking.',
  verification_expired: 'Student verification has expired — renew to book.',
  women_clients_only: 'This creator only accepts women clients.',
  same_institution_only: 'This creator only accepts bookings from students at their institution.',
  first_booking_location:
    'First bookings with this creator must be at a shared public spot or their location.',
  residence_curfew: 'This creator does not do residence calls at this time.',
  late_night_unavailable: 'This service is not available for late-night slots.',
  blocked_language: 'Customisation notes contain language that is not allowed.',
};
